import { useLanguage } from "../../../contexts/LanguageContext";
import { formatMonth, formatNumber, formatPercent } from "../format";
import { EmptyNote, Panel, Skeleton } from "./ui";

const SCALE = ["#F3F4F6", "#E0E7FF", "#A5B4FC", "#4F46E5", "#3730A3"];
const MONTHS = 6;

// Six signup months; cell N = share of that month's signups who scanned at
// least once in month N after joining (month 0 is the signup month).
export default function CohortTable({ cohorts, loading }) {
  const { t, language } = useLanguage();
  const rows = (cohorts || []).slice(-MONTHS);

  // Linear over 0–100% to match the legend. Month 0 is the real share who
  // scanned in their signup month (not a fixed 100%), so it is shaded like
  // every other cell.
  const level = (ratio) => Math.min(4, Math.floor(ratio * 4) + 1);

  return (
    <Panel
      title={t("dashboard.advanced.cohorts.title")}
      subtitle={t("dashboard.advanced.cohorts.subtitle")}
      actions={
        <div className="flex items-center gap-1.5 text-xs text-gray-600" aria-hidden="true">
          <span>0%</span>
          {SCALE.map((c) => (
            <span key={c} className="h-3.5 w-3.5 rounded-[3px]" style={{ background: c }} />
          ))}
          <span>100%</span>
        </div>
      }
    >
      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length === 0 ? (
        <EmptyNote>{t("dashboard.advanced.cohorts.empty")}</EmptyNote>
      ) : (
        <div className="relative overflow-x-auto">
          <table className="w-full min-w-[720px] border-separate border-spacing-1 text-[13px]">
            <thead>
              <tr>
                <th scope="col" className="px-2 py-1.5 text-left text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.cohorts.signedUp")}
                </th>
                <th scope="col" className="px-2 py-1.5 text-right text-xs font-semibold text-gray-600">
                  {t("dashboard.advanced.cohorts.customers")}
                </th>
                {Array.from({ length: MONTHS }, (_, i) => (
                  <th key={i} scope="col" className="px-2 py-1.5 text-center text-xs font-semibold text-gray-600">
                    {t("dashboard.advanced.cohorts.month", { value: i })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.month}>
                  <th scope="row" className="px-2 py-2 text-left font-medium text-gray-900">
                    {formatMonth(row.month, language)}
                  </th>
                  <td className="px-2 py-2 text-right text-gray-700">
                    {formatNumber(row.customers ?? 0, language)}
                  </td>
                  {Array.from({ length: MONTHS }, (_, i) => {
                    const v = row.retention?.[i];
                    if (!Number.isFinite(v)) {
                      return <td key={i} className="rounded-md px-2 py-2.5" />;
                    }
                    const idx = level(v);
                    return (
                      <td
                        key={i}
                        className="rounded-md px-2 py-2.5 text-center font-medium"
                        style={{ background: SCALE[idx], color: idx >= 3 ? "#FFFFFF" : "#1E1B4B" }}
                      >
                        {formatPercent(v, language)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
